import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, BadgeCheck, CheckCircle2, ChevronDown, ChevronRight, Info, ShieldAlert, UserMinus, UserRoundX, Webhook } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

function ApprovalBadge({ row, isCeo, onConfirm }) {
  const info = row.connecteam_approval;
  if (!info?.approved || row.status !== "Open") return null;
  const who = info.by_name ? `by ${info.by_name} on ${info.approved_on}` : "(approver not provided by Connecteam)";
  return (
    <div className="exception-approval" data-testid={`exception-approved-badge-${row.id}`}>
      <small><BadgeCheck size={12} /> Day approved in Connecteam {who}.{info.held === "distance" ? ` Location is over ${LOCATION_MAX_KM} km from the site — resolve with a note.` : info.held === "ceo" ? " Needs your confirmation as CEO." : ""}</small>
      {info.held === "ceo" && isCeo && <button className="button button-ghost" data-testid={`exception-ceo-confirm-${row.id}`} type="button" onClick={() => onConfirm(row)}>I approved this</button>}
    </div>
  );
}

const LOCATION_MAX_KM = 5;

function ExceptionRow({ row, onResolve, onMarkOffice, onConfirmApproval, canMap, isCeo }) {
  const [note, setNote] = useState("");
  const [open, setOpen] = useState(false);
  const [confirmOffice, setConfirmOffice] = useState(false);
  return (
    <tr data-testid={`exception-row-${row.id}`} className={row.requires_ceo_approval ? "exception-ceo" : ""}>
      <td>{row.date}</td>
      <td><strong>{row.person_name}</strong>{!row.provider_id && <><br /><small className="pairing-meta">not mapped to TAGForce</small></>}</td>
      <td>{row.client_name || row.job_title || <span className="pairing-meta">—</span>}</td>
      <td><span className={`status-chip ${row.requires_ceo_approval ? "status-inactive" : "status-pending"}`} data-testid={`exception-type-${row.id}`}>{row.label}</span>{row.requires_ceo_approval && <><br /><small className="pairing-meta"><ShieldAlert size={11} /> CEO approval</small></>}</td>
      <td>{row.detail}<ApprovalBadge row={row} isCeo={isCeo} onConfirm={onConfirmApproval} /></td>
      <td data-testid={`exception-status-${row.id}`}>
        {row.status === "Resolved" ? <><CheckCircle2 size={13} className="ok-icon" /> Resolved by {row.resolved_by_name} {row.resolved_at?.slice(0, 10)}<br /><small>{row.resolution_note}</small></> : open ? (
          <div className="verification-actions">
            <input data-testid={`exception-note-${row.id}`} placeholder="Resolution note (what was corrected in Connecteam)" value={note} onChange={(e) => setNote(e.target.value)} />
            <button className="button button-primary" data-testid={`exception-resolve-${row.id}`} disabled={note.trim().length < 3} type="button" onClick={() => onResolve(row, note.trim())}>Mark resolved</button>
            <button className="provider-profile-link" type="button" onClick={() => setOpen(false)}>Cancel</button>
          </div>
        ) : confirmOffice ? (
          <div className="verification-actions exception-office-confirm" data-testid={`exception-office-confirm-${row.id}`}>
            <p>Mark <strong>{row.person_name}</strong> as office staff? Their clock-ins will count for hours only, every open schedule-based exception they have will auto-resolve, and the change is logged under your name.</p>
            <div className="button-row">
              <button className="button button-primary" data-testid={`exception-office-confirm-yes-${row.id}`} type="button" onClick={() => { setConfirmOffice(false); onMarkOffice(row); }}>Yes, mark as office staff</button>
              <button className="provider-profile-link" data-testid={`exception-office-confirm-no-${row.id}`} type="button" onClick={() => setConfirmOffice(false)}>Cancel</button>
            </div>
          </div>
        ) : (
          <div className="button-row exception-actions">
            <button className="button button-secondary" data-testid={`exception-open-${row.id}`} type="button" onClick={() => setOpen(true)}>Resolve…</button>
            {canMap && row.can_mark_office && <button className="button button-ghost" data-testid={`exception-mark-office-${row.id}`} type="button" title="Only for people with no active client assignment; clock-ins count for hours only" onClick={() => setConfirmOffice(true)}><UserRoundX size={13} /> Mark as office staff</button>}
          </div>
        )}
      </td>
    </tr>
  );
}

export function TimeExceptionsPanel({ onChanged, canMap = false, user = null, initialType = "all" }) {
  const [data, setData] = useState(null);
  const [webhook, setWebhook] = useState(null);
  const [webhookError, setWebhookError] = useState("");
  const [filter, setFilter] = useState("Open");
  const [type, setType] = useState(initialType || "all");
  const [range, setRange] = useState({ from: "", to: "" });
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [showAuto, setShowAuto] = useState(false);
  const [showOffice, setShowOffice] = useState(false);
  const isAdmin = user?.role === "system_administrator";
  const isCeo = (user?.email || "").toLowerCase() === (data?.approvals?.ceo_email || "lesley@theallicockgroup.com");

  const load = useCallback(async () => {
    try {
      const [exceptions, hook] = await Promise.all([
        api.get("/connecteam/time-exceptions", { params: { status_filter: filter, date_from: range.from, date_to: range.to } }),
        api.get("/connecteam/webhooks"),
      ]);
      setData(exceptions.data);
      setWebhook(hook.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }, [filter, range.from, range.to]);

  useEffect(() => {
    load();
  }, [load]);

  async function resolve(row, note) {
    try {
      await api.post(`/connecteam/time-exceptions/${row.id}/resolve`, { note });
      setNotice(`Resolved: ${row.label} · ${row.person_name} · ${row.date}.`);
      setError("");
      await load();
      onChanged?.();
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  async function markOffice(row) {
    try {
      const response = await api.post("/connecteam/office-staff", { name: row.person_name, connecteam_user_id: row.connecteam_user_id, provider_id: row.provider_id || null });
      setNotice(`${row.person_name} marked as office staff${response.data.office_staff_exceptions_resolved ? ` · ${response.data.office_staff_exceptions_resolved} exception(s) auto-resolved` : ""}.`);
      setError("");
      await load();
      onChanged?.();
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  async function unmarkOffice(entry) {
    try {
      await api.delete(`/connecteam/office-staff/${entry.id}`);
      setNotice(`${entry.name} is no longer marked as office staff.`);
      await load();
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  async function confirmApproval(row) {
    try {
      await api.post(`/connecteam/time-exceptions/${row.id}/confirm-approval`);
      setNotice(`Confirmed: you approved ${row.person_name}'s ${row.date} entry in Connecteam.`);
      setError("");
      await load();
      onChanged?.();
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  async function registerWebhook() {
    if (!window.confirm("Register the TAGForce approvals webhook in Connecteam? This is the only change TAGForce makes in Connecteam; it never edits time entries.")) return;
    const origin = window.location.origin && window.location.origin !== "null" ? window.location.origin : new URL(process.env.REACT_APP_BACKEND_URL).origin;
    setWebhookError("");
    try {
      await api.post("/connecteam/webhooks/register", { callback_base: origin });
      setNotice("Connecteam will now tell TAGForce who approves each timesheet.");
      setError("");
      await load();
    } catch (requestError) {
      setWebhookError(formatApiError(requestError));
    }
  }

  if (error && !data) return <p className="form-message error-message" data-testid="exceptions-error">{error}</p>;
  if (!data) return <p className="projection-loading">Loading time exceptions…</p>;
  const rows = data.exceptions.filter((row) => type === "all" || row.type === type);
  return (
    <section className="connecteam-panel" data-testid="connecteam-exceptions">
      <div className="section-heading">
        <div>
          <h2>Time exceptions</h2>
          <p>Refreshed with every sync{data.refreshed_at ? ` (last ${new Date(data.refreshed_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })})` : ""}. Every open exception from the last 42 days is re-checked against current Connecteam data each sync and auto-resolves as "Corrected in Connecteam" when it no longer holds. Schedule-based checks start at each person's first published shift; office staff are exempt. Mark an exception Resolved once the correction has been made in Connecteam; TAGForce never edits time entries.</p>
        </div>
        <div className="verification-toolbar exceptions-toolbar">
          <label>Status<select data-testid="exceptions-status-filter" value={filter} onChange={(e) => setFilter(e.target.value)}><option value="Open">Open</option><option value="Resolved">Resolved</option><option value="all">All</option></select></label>
          <label>Type<select data-testid="exceptions-type-filter" value={type} onChange={(e) => setType(e.target.value)}><option value="all">All types</option>{Object.entries(data.labels).map(([key, label]) => <option key={key} value={key}>{label}{data.open_by_type[key] ? ` (${data.open_by_type[key]})` : ""}</option>)}</select></label>
          <label>From<input data-testid="exceptions-date-from" type="date" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} /></label>
          <label>To<input data-testid="exceptions-date-to" type="date" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} /></label>
        </div>
      </div>
      <div className="exception-periods" data-testid="exceptions-per-period">
        <span className="exception-periods-label">Remaining per pay period</span>
        {data.per_period.length === 0 && <span className="status-chip status-active" data-testid="exceptions-period-none">No open exceptions</span>}
        {data.per_period.map((period) => (
          <button key={period.key} type="button" className={`exception-period-chip ${range.from === period.start && range.to === period.end ? "active" : ""}`} data-testid={`exceptions-period-${period.key}`} onClick={() => setRange(range.from === period.start && range.to === period.end ? { from: "", to: "" } : { from: period.start, to: period.end })}>
            {period.label} <em>{period.open}</em>
          </button>
        ))}
      </div>
      {!data.schedule_in_use && <p className="form-message connecteam-info" data-testid="exceptions-no-schedule"><Info size={13} /> The Connecteam schedule has no shifts in the sync window, so schedule-based checks (clock-in without a shift, shift with no clock-in, late clock-in) are paused until shifts are published in Connecteam.</p>}
      <div className="connecteam-webhook-card" data-testid="connecteam-webhook-card">
        <Webhook size={15} />
        <div>
          <strong>Connecteam approvals</strong>
          <p>
            {data.approvals.approved_days} approved timesheet day{data.approvals.approved_days === 1 ? "" : "s"} in the window resolve late clock-ins, hours over schedule, clock-ins without a shift and location flags within {data.approvals.location_max_km} km.
            {webhook?.registered
              ? ` Approver webhook active since ${webhook.registered_at?.slice(0, 10)} (${webhook.events_received} event${webhook.events_received === 1 ? "" : "s"}${webhook.failed_verifications ? `, ${webhook.failed_verifications} rejected call${webhook.failed_verifications === 1 ? "" : "s"}` : ""}).`
              : " Connecteam's API does not say who approved; register the webhook on the live site so CEO-approval items can clear automatically when you approve them."}
          </p>
          {webhookError && <p className="form-message error-message" data-testid="connecteam-webhook-error">Registration failed: {webhookError}</p>}
        </div>
        {isAdmin && !webhook?.registered && <button className="button button-secondary" data-testid="connecteam-webhook-register" type="button" onClick={registerWebhook}>Register webhook</button>}
      </div>
      {error && <p className="form-message error-message" data-testid="exceptions-error">{error}</p>}
      {notice && <p className="form-message success-message" data-testid="exceptions-notice">{notice}</p>}
      <div className="requests-table-panel">
        <table className="requests-table connecteam-table" data-testid="exceptions-table">
          <thead><tr><th>Date</th><th>Person</th><th>Site / client</th><th>Exception</th><th>Detail</th><th>Resolution</th></tr></thead>
          <tbody>
            {rows.map((row) => <ExceptionRow key={row.id} row={row} onResolve={resolve} onMarkOffice={markOffice} onConfirmApproval={confirmApproval} canMap={canMap} isCeo={isCeo} />)}
            {!rows.length && <tr><td colSpan={6} className="provider-empty-state" data-testid="exceptions-empty"><AlertTriangle size={13} /> No {filter === "all" ? "" : filter.toLowerCase()} time exceptions{type !== "all" ? " of this type" : ""}{range.from || range.to ? " in this date range" : ""}.</td></tr>}
          </tbody>
        </table>
      </div>

      <section className="exception-collapsible" data-testid="exceptions-office-staff">
        <button type="button" className="exception-collapsible-toggle" data-testid="exceptions-office-staff-toggle" onClick={() => setShowOffice((v) => !v)}>
          {showOffice ? <ChevronDown size={15} /> : <ChevronRight size={15} />} Office staff — hours only, no schedule-based exceptions ({data.office_staff.built_in.length + data.office_staff.marked.length})
        </button>
        {showOffice && (
          <ul className="exception-office-list" data-testid="exceptions-office-list">
            {data.office_staff.built_in.map((p) => <li key={p.name}><strong>{p.name}</strong> <small className="pairing-meta">built-in</small></li>)}
            {data.office_staff.marked.map((p) => (
              <li key={p.id} data-testid={`office-staff-${p.id}`}>
                <strong>{p.name}</strong> <small className="pairing-meta">marked by {p.added_by_name} {p.added_at?.slice(0, 10)}</small>
                {canMap && <button type="button" className="button button-ghost" data-testid={`office-staff-remove-${p.id}`} onClick={() => unmarkOffice(p)}><UserMinus size={13} /> Remove</button>}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="exception-collapsible" data-testid="exceptions-auto-resolved">
        <button type="button" className="exception-collapsible-toggle" data-testid="exceptions-auto-resolved-toggle" onClick={() => setShowAuto((v) => !v)}>
          {showAuto ? <ChevronDown size={15} /> : <ChevronRight size={15} />} Auto-resolved for audit ({data.auto_resolved_count}){range.from || range.to ? " in this date range" : ""}
        </button>
        {showAuto && Object.entries(data.auto_reason_labels).map(([reason, heading]) => {
          const group = data.auto_resolved.filter((row) => row.auto_reason === reason);
          return (
            <div key={reason} className="exception-audit-group" data-testid={`exceptions-audit-${reason}`}>
              <h3 className="exception-audit-heading">{heading} <em>{group.length}</em></h3>
              {group.length > 0 && (
                <div className="requests-table-panel">
                  <table className="requests-table connecteam-table" data-testid={`exceptions-audit-table-${reason}`}>
                    <thead><tr><th>Date</th><th>Person</th><th>Site / client</th><th>Exception</th><th>Auto-resolved</th></tr></thead>
                    <tbody>
                      {group.map((row) => (
                        <tr key={row.id} data-testid={`auto-resolved-row-${row.id}`}>
                          <td>{row.date}</td><td>{row.person_name}</td><td>{row.client_name || row.job_title || "—"}</td><td>{row.label}</td>
                          <td><small>{row.resolution_note} · {row.resolved_at?.slice(0, 10)}</small></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {group.length === 0 && <p className="pairing-meta exception-audit-empty">Nothing{range.from || range.to ? " in this date range" : ""}.</p>}
            </div>
          );
        })}
      </section>
    </section>
  );
}
